import {
  defineQuery,
  defineQueryOptions,
  useQueryCache,
  type EntryKey,
} from '@pinia/colada'
import type { PaAPI } from '@rialtic/types'
import { STALE_TIME } from '@rialtic/utils'
import { workspaceQuery } from './workspace'
import type { Result } from 'neverthrow'
import { ok, err } from 'neverthrow'

export type PaginatedPolicyListDetails = PaAPI.Get['/policy/list-details']

export type PolicyListDetailsItem = PaginatedPolicyListDetails['items'][number]

export type PolicyIntegrityIssueRow =
  PaAPI.Schemas['PolicyIntegrityIssueRowDto']

export type PolicyIntegritySeverity = NonNullable<
  PolicyListDetailsItem['severity']
>

// Operations type accepts severity as an array; schema type lists a single value.
// We expose the array form to consumers so chips/multi-select filters work.
export type PolicyListDetailsQuery = Omit<
  PaAPI.Schemas['PolicyListDetailsQueryDto'],
  'severity'
> & {
  severity?: PolicyIntegritySeverity[]
}

export type PolicyIntegrityCount =
  PaAPI.Get['/policy/list-details/integrity/count']

// Pre-computed processed data with expensive operations moved out of the grid
export type ProcessedPolicy = PolicyListDetailsItem & {
  latest_review_status: string
  history_display: string
  engines_display: string
  topic_name: string
  edit_types_display: string[]
}

/** Policy Queries */
const POLICY_QUERY_KEYS_ROOT: EntryKey = [
  'paapi',
  'content-data',
  'policies',
] as const
export const POLICY_QUERY_KEYS = {
  root: POLICY_QUERY_KEYS_ROOT,

  policyListDetailsRoot: [...POLICY_QUERY_KEYS_ROOT, 'list-details'] as const,

  policyListDetails: (query: string[]) =>
    [...POLICY_QUERY_KEYS_ROOT, 'list-details', ...query] as const,

  policyIntegrityCount: [
    ...POLICY_QUERY_KEYS_ROOT,
    'list-details',
    'integrity',
    'count',
  ] as const,

  policyById: (id: string) => [...POLICY_QUERY_KEYS_ROOT, id] as const,

  policyByIdWithInclude: (id: string) =>
    [...POLICY_QUERY_KEYS_ROOT, id, 'include'] as const,

  policyClientStatus: (policyId: string) =>
    [...POLICY_QUERY_KEYS_ROOT, policyId, 'client-status'] as const,

  policyInsightTypes: (policyId: string) =>
    [...POLICY_QUERY_KEYS_ROOT, policyId, 'insight-types'] as const,

  policyPrimaryReferenceDoc: (policyId: string) =>
    [
      ...POLICY_QUERY_KEYS_ROOT,
      policyId,
      'primary-reference-document',
    ] as const,

  policyReviewList: [...POLICY_QUERY_KEYS_ROOT, 'review-list'] as const,

  policyReviewStatusOptions: [
    ...POLICY_QUERY_KEYS_ROOT,
    'review-status-options',
  ] as const,

  policyReviewHistory: (policyId: string) =>
    [...POLICY_QUERY_KEYS_ROOT, policyId, 'review-history'] as const,

  policyReferenceDocuments: (policyId: string) =>
    [...POLICY_QUERY_KEYS_ROOT, policyId, 'reference-documents'] as const,
}

type PolicyReviewMinimal = PaAPI.Schemas['PolicyReviewWithoutRelations']

export type PolicyReviewStatusOption = {
  id: string
  status: string
}

export type PolicyReviewHistoryEntry = PolicyReviewMinimal & {
  status_label: string
}

type PolicyReferenceDocumentSourceType = {
  id: string
  name: string
} | null

type PolicyReferenceDocumentRegion = {
  region_id: string
}

export type PolicyReferenceDocument = {
  id: string
  // Global reference_document.is_primary flag (document-level, across all policies).
  /** @deprecated */
  is_primary: boolean
  // Per-policy primary designation from reference_document_policy.is_primary for
  // this policy. Use this for primary state on the policy's sources view.
  is_primary_for_policy: boolean
  policy_excerpt_count: number
  source: string
  description: string | null
  source_url: string | null
  document_type: PaAPI.Schemas['ReferenceDocument']['document_type']
  source_type_level_1: PolicyReferenceDocumentSourceType
  source_type_level_2: PolicyReferenceDocumentSourceType
  reference_document_region: PolicyReferenceDocumentRegion[]
}

export const usePolicies = defineQuery({
  key: POLICY_QUERY_KEYS.root,
  query: async () => {
    const client = useRequestFetch()

    const LIMIT = 500
    const maxCount = 6000
    const pages = Math.ceil(maxCount / LIMIT)

    const reqs = Array.from({ length: pages }, (_, i) => {
      return client<PaAPI.Get['/policy']>(
        `/api/paapi/policy?result=min&limit=${LIMIT}&offset=${i * LIMIT}` as const,
      )
    })

    const results = await Promise.all(reqs)
    const data = results.flat()

    return data
  },

  staleTime: STALE_TIME.FIVE_MINUTES,
})

export const policyListDetailsQuery = defineQueryOptions(
  (query: PolicyListDetailsQuery = {}) => ({
    // key the query based on user defined query params
    key: POLICY_QUERY_KEYS.policyListDetails(
      Object.entries(query)
        .reduce((acc, [key, value]) => {
          if (value !== null && value !== undefined) {
            acc.push(
              `${key}=${typeof value === 'object' ? JSON.stringify(value) : value}`,
            )
          }
          return acc
        }, [] as string[])
        .sort(),
    ),
    query: () => {
      const client = useRequestFetch()
      return client<PaginatedPolicyListDetails>(
        '/api/paapi/policy/list-details',
        { query },
      )
    },
    staleTime: STALE_TIME.FIVE_MINUTES,
    // garbage collection after five minutes
    gcTime: STALE_TIME.FIVE_MINUTES,
  }),
)

export const policyIntegrityCountQuery = defineQueryOptions(() => ({
  key: POLICY_QUERY_KEYS.policyIntegrityCount,
  query: () => {
    const client = useRequestFetch()
    return client<PolicyIntegrityCount>(
      '/api/paapi/policy/list-details/integrity/count',
    )
  },
  staleTime: STALE_TIME.FIVE_MINUTES,
}))

export const policyById = defineQueryOptions((id: string) => {
  return {
    key: POLICY_QUERY_KEYS.policyById(id),
    enabled: Boolean(id),
    query: () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/policy/{id}']>(
        `/api/paapi/policy/${id}` as const,
      )
    },
    staleTime: STALE_TIME.ONE_HOUR,
  }
})

export const policyByIdWithInclude = defineQueryOptions((id: string) => {
  return {
    key: POLICY_QUERY_KEYS.policyByIdWithInclude(id),
    enabled: Boolean(id),
    query: () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/policy/{id}']>(
        `/api/paapi/policy/${id}` as const,
        {
          query: {
            include: true,
          },
        },
      )
    },
    staleTime: STALE_TIME.ONE_HOUR,
  }
})

const policyReviewListQuery = defineQueryOptions({
  key: POLICY_QUERY_KEYS.policyReviewList,
  query: () => {
    const client = useRequestFetch()
    return client<PolicyReviewMinimal[]>('/api/paapi/policy-review')
  },
  staleTime: STALE_TIME.FIFTEEN_MINUTES,
  persist: false,
})

const policyReviewStatusOptionsQuery = defineQueryOptions({
  key: POLICY_QUERY_KEYS.policyReviewStatusOptions,
  query: async (): Promise<PolicyReviewStatusOption[]> => {
    const client = useRequestFetch()
    const reviewStatuses = await client<PaAPI.Get['/review-status']>(
      '/api/paapi/review-status',
    )

    return reviewStatuses
      .map(({ id, status }) => ({
        id,
        status,
      }))
      .toSorted((a, b) => a.status.localeCompare(b.status))
  },
  staleTime: STALE_TIME.FIFTEEN_MINUTES,
  persist: false,
})

export const usePolicyReviewStatusOptions = defineQuery(
  policyReviewStatusOptionsQuery,
)

export const policyReviewHistory = defineQueryOptions((policyId: string) => {
  return {
    key: POLICY_QUERY_KEYS.policyReviewHistory(policyId),
    enabled: Boolean(policyId),
    persist: false,
    query: async (): Promise<PolicyReviewHistoryEntry[]> => {
      const queryCache = useQueryCache()

      const [{ data: reviewList }, { data: reviewStatusOptions }] =
        await Promise.all([
          queryCache.refresh(queryCache.ensure(policyReviewListQuery)),
          queryCache.refresh(queryCache.ensure(policyReviewStatusOptionsQuery)),
        ])

      const statusById = new Map(
        (reviewStatusOptions ?? []).map((statusOption) => [
          statusOption.id,
          statusOption.status,
        ]),
      )

      const reviewHistory =
        reviewList
          ?.filter((review) => review.policy_id === policyId)
          .map((review) => ({
            ...review,
            status_label: statusById.get(review.status_id) ?? review.status_id,
          }))
          .toSorted(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          ) ?? []

      return reviewHistory
    },
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }
})

export const policyClientStatusSummary = defineQueryOptions((id: string) => {
  return {
    key: POLICY_QUERY_KEYS.policyClientStatus(id),
    enabled: Boolean(id),
    query: async () => {
      const queryCache = useQueryCache()
      await queryCache.ensure(workspaceQuery)
      const client = useRequestFetch()
      return client<PaAPI.Get['/policy/{id}/status-summary']>(
        `/api/paapi/policy/${id}/status-summary` as const,
      )
    },
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }
})

const validatePrimarySources = (
  count: number,
  {
    hasPrimaryDocument,
    hasPrimaryDataset,
  }: { hasPrimaryDocument?: boolean; hasPrimaryDataset?: boolean },
): Result<true, string> => {
  if (!count) {
    return err('A primary reference source is required to publish this policy.')
  }

  if (count === 1) {
    return ok(true)
  }

  if (count === 2) {
    if (hasPrimaryDocument && hasPrimaryDataset) {
      return ok(true)
    }

    return err(
      `Maximum of 2 primary reference sources allowed, one document and one dataset. Found multiple ${hasPrimaryDocument ? 'documents' : 'datasets'}`,
    )
  }

  return err(
    `Maximum of 2 primary reference sources allowed, one document and one dataset. Found ${count}.`,
  )
}

export type PolicySourceDoc = Omit<
  PaAPI.Schemas['ResponseReferenceDocumentMinimumDto'],
  'is_primary'
>
interface PolicyPrimarySources {
  isValid: boolean
  document: (PolicySourceDoc & { document_type: 'DOCUMENT' }) | null
  dataset: (PolicySourceDoc & { document_type: 'DATASET' }) | null
  validationMessage?: string
}
export const policyPrimaryReferenceDoc = defineQueryOptions((id: string) => {
  return {
    key: POLICY_QUERY_KEYS.policyPrimaryReferenceDoc(id),
    enabled: Boolean(id),
    persist: false,
    query: async (): Promise<PolicyPrimarySources> => {
      const queryCache = useQueryCache()

      const { data: refDocsData } = await queryCache.refresh(
        queryCache.ensure(policyReferenceDocuments(id)),
      )

      const primaryDocs = refDocsData?.filter(
        (doc) => doc.is_primary_for_policy,
      )

      if (!primaryDocs?.length) {
        return {
          isValid: false,
          document: null,
          dataset: null,
          validationMessage:
            'A primary reference source is required to publish this policy.',
        }
      }

      const count = primaryDocs.length

      const isDocument = (
        doc: PolicySourceDoc,
      ): doc is PolicySourceDoc & { document_type: 'DOCUMENT' } =>
        doc.document_type === 'DOCUMENT'
      const isDataset = (
        doc: PolicySourceDoc,
      ): doc is PolicySourceDoc & { document_type: 'DATASET' } =>
        doc.document_type === 'DATASET'

      const primaryDocument = primaryDocs.find(isDocument) ?? null
      const primaryDataset = primaryDocs.find(isDataset) ?? null

      const validationResult = validatePrimarySources(count, {
        hasPrimaryDocument: Boolean(primaryDocument),
        hasPrimaryDataset: Boolean(primaryDataset),
      })

      return {
        isValid: validationResult.isOk(),
        document: primaryDocument,
        dataset: primaryDataset,
        validationMessage: validationResult.isErr()
          ? validationResult.error
          : undefined,
      }
    },
    staleTime: STALE_TIME.FIVE_SECONDS,
  }
})

export const policyReferenceDocuments = defineQueryOptions((id: string) => {
  return {
    key: POLICY_QUERY_KEYS.policyReferenceDocuments(id),
    enabled: Boolean(id),
    persist: false,
    placeholderData: [],
    query: async (): Promise<PolicySourceDoc[]> => {
      const client = useRequestFetch()

      const docs = await client<PolicySourceDoc[]>(
        `/api/paapi/reference-document?result=min&policy_id=${id}`,
      )

      return (docs ?? []).toSorted((left, right) => {
        if (left.is_primary_for_policy !== right.is_primary_for_policy) {
          return left.is_primary_for_policy ? -1 : 1
        }

        if (left?.document_type !== right?.document_type) {
          return left?.document_type === 'DOCUMENT' ? -1 : 1
        }

        if (left?.source !== right?.source) {
          return left?.source.localeCompare(right?.source)
        }

        return left.id.localeCompare(right.id)
      })
    },
    staleTime: STALE_TIME.ONE_HOUR,
  }
})
